/*
 * gs-facet-search.js — Goods Scene
 * Type-to-filter box inside any facet with many values.
 *
 * Injected client-side only. snippets/facets.liquid is NOT modified.
 * Shopify's checkboxes, param_names, url_to_add/url_to_remove and counts
 * are never touched — this only sets `hidden` on <li> elements.
 *
 * THREE HARD CONSTRAINTS, verified by reading Dawn's assets/facets.js:
 *  1. Dawn listens for `input` on the whole facet form (500ms debounce).
 *     Every keystroke here would trigger a full AJAX re-render, so the
 *     input MUST stopPropagation().
 *  2. Dawn builds the filter URL with new FormData(form). The input MUST
 *     NOT have a `name`, or typed text lands in the shopper's URL.
 *  3. Dawn replaces the INNER HTML of each .js-filter (and the whole
 *     .facets-wrap / .mobile-facets__list of the clicked filter), so the
 *     box is destroyed on every filter change and must be rebuilt.
 *     The .js-filter element itself persists, so observers attached to it
 *     survive and never need re-binding.
 */
(function () {
  'use strict';

  var THRESHOLD = 12; // values required before a search box appears

  var cfg =
    document.currentScript ||
    document.querySelector('script[data-gs-facet-search]');
  var ds = (cfg && cfg.dataset) || {};
  var gsLabel = function (key) { return window.gsT ? window.gsT('facets.' + key) : ''; };
  var LABEL_SEARCH = ds.gsSearchLabel || gsLabel('search');
  var LABEL_NONE = ds.gsNoneLabel || gsLabel('no_matching');
  var LABEL_NONE_SELECTED =
    ds.gsNoneSelectedLabel || gsLabel('no_matching_selected');

  // Typed text per filter, keyed by param_name, so it survives Dawn's
  // re-render instead of being wiped on every checkbox click.
  var queries = Object.create(null);

  // No re-entrancy flag is needed. Our own writes converge on their own:
  // setting `li.hidden` or `input.value` to a value they already hold emits
  // no MutationRecord, so the observer stops firing after one settling pass.
  // A flag here would only look like protection without providing any.

  /* ---------- helpers ---------- */

  // Desktop renders the value list TWICE: the real one and a `.no-js-list`
  // fallback. Filtering the fallback would silently break no-JS shoppers.
  function listsIn(root) {
    return root.querySelectorAll(
      'ul.facets__list:not(.no-js-list), ul.mobile-facets__list'
    );
  }

  function keyFor(list) {
    var input = list.querySelector('input[name]');
    return input ? input.name : '';
  }

  function itemsOf(list) {
    var out = [];
    for (var n = list.firstElementChild; n; n = n.nextElementSibling) {
      if (n.tagName === 'LI') out.push(n);
    }
    return out;
  }

  // Label node is `.facet-checkbox__text` on BOTH desktop and mobile.
  // Its text reads "Z50 Ultra (12)" — strip the trailing count.
  // Computed once per <li> and cached. Dawn hands us brand new <li> elements
  // on every re-render, so the cache can never go stale.
  function textOf(li) {
    if (li.dataset.gsLabel === undefined) {
      var span = li.querySelector('.facet-checkbox__text');
      var t = (span ? span.textContent : li.textContent) || '';
      li.dataset.gsLabel = t
        .replace(/\s*\([\d,.\s]+\)\s*$/, '')
        .trim()
        .toLowerCase();
    }
    return li.dataset.gsLabel;
  }

  /* ---------- filtering ---------- */

  function apply(list, raw) {
    var q = (raw || '').trim().toLowerCase();
    var items = itemsOf(list);

    // Two different questions, so two different counters:
    // `matches` = how many labels actually match what was typed.
    // `checkedVisible` = whether a preserved selection is on screen.
    // Counting a preserved selection as a "match" would tell the shopper
    // their query found something when it found nothing.
    var matches = 0;
    var checkedVisible = false;

    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      // A checked value is ALWAYS visible. If it were hidden the shopper
      // would see a result count they cannot explain and cannot undo.
      var checked = !!li.querySelector('input:checked');
      var textMatch = textOf(li).indexOf(q) !== -1;
      if (textMatch) matches++;

      var visible = !q || checked || textMatch;
      li.hidden = !visible;
      if (visible && checked) checkedVisible = true;
    }

    var none = list.nextElementSibling;
    if (none && none.classList.contains('gs-facet-search__none')) {
      if (matches > 0) {
        none.hidden = true;
      } else {
        var msg = checkedVisible ? LABEL_NONE_SELECTED : LABEL_NONE;
        // Only write when it actually changes — keeps the observer quiet.
        if (none.textContent !== msg) none.textContent = msg;
        none.hidden = false;
      }
    }
  }

  /* ---------- injection ---------- */

  function decorate(list) {
    if (itemsOf(list).length < THRESHOLD) return;

    var prev = list.previousElementSibling;
    var box =
      prev && prev.classList.contains('gs-facet-search') ? prev : null;

    var key = keyFor(list);

    if (!box) {
      box = document.createElement('div');
      box.className = 'gs-facet-search';

      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'gs-facet-search__input';
      input.placeholder = LABEL_SEARCH;
      input.setAttribute('aria-label', LABEL_SEARCH);
      input.setAttribute('autocomplete', 'off');
      // Deliberately NO name attribute — see constraint 2 above.

      input.addEventListener('input', function (event) {
        event.stopPropagation(); // constraint 1
        queries[key] = input.value;
        apply(list, input.value);
      });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
      });

      box.appendChild(input);
      list.parentNode.insertBefore(box, list);

      var none = document.createElement('p');
      none.className = 'gs-facet-search__none';
      none.setAttribute('role', 'status');
      none.textContent = LABEL_NONE;
      none.hidden = true;
      list.parentNode.insertBefore(none, list.nextSibling);
    }

    var field = box.querySelector('.gs-facet-search__input');
    var saved = queries[key] || '';
    if (field.value !== saved) field.value = saved;
    apply(list, saved);
  }

  function decorateAll(root) {
    var lists = listsIn(root);
    for (var i = 0; i < lists.length; i++) decorate(lists[i]);
  }

  /* ---------- lifecycle ---------- */

  function scan() {
    var filters = document.querySelectorAll('.js-filter');
    for (var i = 0; i < filters.length; i++) {
      var el = filters[i];
      if (!el.gsFacetSearchBound) {
        el.gsFacetSearchBound = true;
        // .js-filter survives every Dawn re-render, so one observer is enough.
        new MutationObserver(function (records) {
          var target = records[0] && records[0].target;
          var host = target && target.closest && target.closest('.js-filter');
          if (host) decorateAll(host);
        }).observe(el, { childList: true, subtree: true });
      }
      decorateAll(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();

/*
 * MODEL FILTER — FALLBACK ACTIVE CHIP
 *
 * WHY THIS EXISTS. Shopify renders at most 100 values per filter. The Model
 * filter (goods.compatible_models) has several hundred, and the Customer
 * Account "My Devices" feature links straight to
 *   /collections/all?filter.p.m.goods.compatible_models=<Metaobject GID>
 * When that model is outside the 100 Shopify returned, the result set IS
 * filtered, but the value is missing from filter.active_values — and
 * snippets/facets.liquid builds every active chip by looping active_values.
 * So no chip is drawn, "Remove all" stays hidden (it only shows beside a
 * chip), and the shopper sees a filtered list with no explanation and no way
 * out. This block draws the missing chip. It changes nothing about filtering.
 *
 * WHAT IT DOES. For every Model value in the page URL that has NO native chip,
 * it adds one chip to each .active-facets row, in the same markup Dawn uses,
 * just before "Remove all". Its link is the current URL minus that one Model
 * value (and minus `page`); every other filter, the sort order and the search
 * terms are kept. The link sits in a <facet-remove>, so Dawn handles the click
 * exactly like a native chip — AJAX re-render, history, loading state.
 *
 * HOW A NATIVE CHIP IS RECOGNISED, so there is never a duplicate. A native
 * chip for value V links to "the current filters minus V". So a chip already
 * exists for V when some chip link in the row carries exactly the current
 * filter.* parameters with that one value taken out. Only then is nothing
 * added. This is read from the links Shopify generated, not from labels.
 *
 * THE LABEL IS GENERIC ON PURPOSE. The model's name is not in the page: the
 * value is absent from the rendered list, which is the whole problem, and
 * nothing may be fetched to find it. Override the text with
 * data-gs-model-fallback-label on the script tag if needed.
 *
 * LIFECYCLE. Dawn replaces the innerHTML of .active-facets-desktop and
 * .active-facets-mobile on every filter change; the rows themselves persist.
 * One observer per row rebuilds the chip. The work is idempotent — a second
 * pass finds the chip in place and writes nothing — so it settles by itself.
 *
 * Without JavaScript there is no fallback chip. Dawn's filter UI is already
 * JavaScript-driven, and the mobile drawer's own "Remove all" still works.
 */
(function () {
  'use strict';

  var PARAM = 'filter.p.m.goods.compatible_models';
  var MARK = 'data-gs-model-fallback';

  var cfg = document.querySelector('script[data-gs-facet-search]');
  var ds = (cfg && cfg.dataset) || {};
  var gsLabel = function (key) { return window.gsT ? window.gsT('facets.' + key) : ''; };
  var LABEL = ds.gsModelFallbackLabel || gsLabel('model_selected');
  var LABEL_REMOVE = ds.gsModelRemoveLabel || gsLabel('remove_filter');

  // Dawn's own snippets/icon-close-small.liquid, so the chip matches the rest.
  var ICON =
    '<svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function esc(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Every filter.* parameter as a sorted list of "name=value" strings, so two
  // URLs can be compared regardless of parameter order or encoding.
  // EMPTY VALUES ARE SKIPPED. Dawn submits the whole facet form, so a filter
  // picked through the UI leaves filter.v.price.gte= and filter.v.price.lte=
  // in the page URL, while Shopify's own chip links omit them. An empty
  // parameter filters nothing; counting it made a native chip look missing and
  // produced a duplicate fallback chip.
  function filterEntries(params) {
    var out = [];
    params.forEach(function (value, name) {
      if (name.indexOf('filter.') === 0 && value !== '') out.push(name + '=' + value);
    });
    return out.sort();
  }

  function sameList(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  function queryOf(href) {
    var at = href.indexOf('?');
    var query = at === -1 ? '' : href.slice(at + 1);
    var hash = query.indexOf('#');
    return new URLSearchParams(hash === -1 ? query : query.slice(0, hash));
  }

  function hasNativeChip(row, current, gid) {
    var want = filterEntries(current);
    var at = want.indexOf(PARAM + '=' + gid);
    if (at === -1) return true; // not in the URL: nothing to add
    want.splice(at, 1);

    var links = row.querySelectorAll('a.active-facets__button');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (link.closest('[' + MARK + ']')) continue; // ours
      var have = filterEntries(queryOf(link.getAttribute('href') || ''));
      if (sameList(have, want)) return true;
    }
    return false;
  }

  function removeHref(current, gid) {
    var next = new URLSearchParams();
    var dropped = false;
    current.forEach(function (value, name) {
      if (name === 'page') return; // the page number belongs to the old result set
      if (!dropped && name === PARAM && value === gid) {
        dropped = true;
        return;
      }
      next.append(name, value);
    });
    var query = next.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  function chipHtml(gid, href) {
    return (
      '<facet-remove ' + MARK + '="' + esc(gid) + '">' +
      '<a href="' + esc(href) + '" class="active-facets__button active-facets__button--light">' +
      '<span class="active-facets__button-inner button button--tertiary">' +
      esc(LABEL) +
      ICON +
      '<span class="visually-hidden">' + esc(LABEL_REMOVE) + '</span>' +
      '</span>' +
      '</a>' +
      '</facet-remove>'
    );
  }

  function syncRow(row, current, gids) {
    var mine = row.querySelectorAll('[' + MARK + ']');
    var byGid = Object.create(null);
    var i;

    for (i = 0; i < mine.length; i++) {
      var id = mine[i].getAttribute(MARK);
      if (gids.indexOf(id) === -1 || byGid[id]) {
        mine[i].remove(); // value no longer active, or a stray duplicate
      } else {
        byGid[id] = mine[i];
      }
    }

    for (i = 0; i < gids.length; i++) {
      var gid = gids[i];
      var chip = byGid[gid];

      if (hasNativeChip(row, current, gid)) {
        if (chip) chip.remove();
        continue;
      }

      var href = removeHref(current, gid);

      if (chip) {
        var link = chip.querySelector('a');
        // Only write when it actually changes — keeps the observer quiet.
        if (link && link.getAttribute('href') !== href) link.setAttribute('href', href);
        continue;
      }

      var clearAll = row.querySelector('facet-remove.active-facets__button-wrapper');
      if (clearAll && clearAll.parentNode === row) {
        clearAll.insertAdjacentHTML('beforebegin', chipHtml(gid, href));
      } else {
        row.insertAdjacentHTML('beforeend', chipHtml(gid, href));
      }
    }
  }

  function sync() {
    var current = new URLSearchParams(window.location.search);
    var all = current.getAll(PARAM);
    var gids = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i] && gids.indexOf(all[i]) === -1) gids.push(all[i]);
    }

    var rows = document.querySelectorAll('.active-facets');
    for (var r = 0; r < rows.length; r++) syncRow(rows[r], current, gids);
  }

  function bind() {
    var rows = document.querySelectorAll('.active-facets');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row.gsModelChipBound) {
        row.gsModelChipBound = true;
        // The row survives every Dawn re-render; only its children are swapped.
        new MutationObserver(sync).observe(row, { childList: true });
      }
    }
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);
})();
